import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CultureHubCard from "./Cards/CultureHubCard";
import CultureHubCardSkeleton from "./Cards/CultureHubCardSkeleton";
import visuallyHiddenSx from "../../utils/a11y/visuallyHiddenSx";
import { pluralize } from "../../utils/a11y/ptBr";

const DEFAULT_SKELETON_COUNT = 16;
// varied widths so the placeholders don't look like a uniform pattern
const SKELETON_TITLE_WIDTHS = ["60%", "45%", "70%", "55%"];
const SKELETON_FORMAT_WIDTHS = ["30%", "40%", "25%"];
const RESULTS_HEADING_ID = "culturehub-results-heading";

const gridSx = {
  display: "grid",
  // auto-fill (not auto-fit) reserves empty tracks instead of letting existing cards stretch into
  // them, so cards stay exactly 280px while 4 fit per row, regardless of how many results there
  // are; once the row can't fit 4 (maxWidth below 1168px), fewer tracks are reserved and the 1fr
  // max lets cards grow/shrink to fill that row. min(280px, 100%) avoids overflow on narrow screens
  gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
  gap: "16px",
  width: "100%",
  // 4 * 280px + 3 * 16px gap: caps columns at 4 even on very wide screens
  maxWidth: "1168px",
  listStyle: "none",
  padding: 0,
  margin: "32px 0 24px 0",
};

const getStatusMessage = (loading, results, term) => {
  const forTerm = term ? ` para "${term}"` : "";

  if (loading) {
    return term
      ? `Buscando "${term}" no HUB de Cultura…`
      : "Buscando resultados no HUB de Cultura…";
  }
  if (results === null) return "";
  if (!results.length) return `Nenhum resultado encontrado${forTerm}.`;
  return `${results.length} ${pluralize(
    results.length,
    "resultado encontrado",
    "resultados encontrados"
  )}${forTerm}.`;
};

const CultureHubSearchResults = ({
  results = null,
  loading = false,
  term = "",
  rowsPerPage = DEFAULT_SKELETON_COUNT,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const statusMessage = (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      sx={visuallyHiddenSx}
    >
      {getStatusMessage(loading, results, term)}
    </Box>
  );

  if (loading) {
    return (
      <>
        {statusMessage}
        <Box aria-hidden="true" sx={gridSx}>
          {Array.from({ length: rowsPerPage }, (_, index) => (
            <Box key={index}>
              <CultureHubCardSkeleton
                titleWidth={
                  SKELETON_TITLE_WIDTHS[index % SKELETON_TITLE_WIDTHS.length]
                }
                formatWidth={
                  SKELETON_FORMAT_WIDTHS[index % SKELETON_FORMAT_WIDTHS.length]
                }
              />
            </Box>
          ))}
        </Box>
      </>
    );
  }

  if (results === null) return statusMessage;

  if (!results.length) {
    return (
      <>
        {statusMessage}
        <Typography
          sx={{
            fontFamily: "inherit",
            color: "#5c5c5c",
            fontSize: "32px",
            textAlign: "center",
            mt: "20vh",
          }}
        >
          Nenhum resultado encontrado.
        </Typography>
      </>
    );
  }

  return (
    <>
      {statusMessage}
      <Box component="h2" id={RESULTS_HEADING_ID} sx={visuallyHiddenSx}>
        Resultados da busca
      </Box>
      <Box
        component="ul"
        role="list"
        aria-labelledby={RESULTS_HEADING_ID}
        sx={gridSx}
      >
        {results.map((item) => (
          <Box component="li" key={item.id}>
            <CultureHubCard
              title={item.title}
              isNew={item.isNew}
              parecerCount={item.parecerCount}
              format={item.format?.name}
              onClick={() =>
                navigate({
                  pathname: `/hub-cultura/${item.id}`,
                  search: location.search,
                })
              }
            />
          </Box>
        ))}
      </Box>
    </>
  );
};

export default CultureHubSearchResults;
